import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchRewardsClass,
  createRewardsClass,
  updateRewardsClass,
  fetchMccList,
  fetchMerchantList,
  fetchChainIdList,
} from '@/api/rewards-class'
import { useAuthQuery } from '@/hooks/use-auth-query'

export const useFetchRewardsClass = ({ page = 1 } = {}) => {
  return useAuthQuery({
    queryKey: ['rewardsClass', page],
    queryFn: () => fetchRewardsClass(page),
    keepPreviousData: true,
    onError: error => {
      console.error('Error fetching rewards class:', error.message)
    },
  })
}

export function useCreateClass() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createClass'],
    mutationFn: createRewardsClass,
    onMutate: async newClassData => {
      const previousClasses = queryClient.getQueryData(['rewardClass'])
      queryClient.setQueryData(['rewardClass'], (current = []) => [...current, newClassData])
      return { previousClasses }
    },
    onError: context => {
      queryClient.setQueryData(['rewardClass'], context.previousClasses)
    },
    onSettled: () => {
      queryClient.invalidateQueries(['rewardClass'])
    },
  })
}

export const useUpdateRewardsClass = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ rewardClass, updateData }) => {
      const response = await updateRewardsClass({ rewardClass, updateData })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['rewardsClass'])
    },
  })
}

export const useFetchReferences = ({ page, perPage, search } = {}) => {
  return useAuthQuery({
    queryKey: ['referencesList'],
    queryFn: async () => {
      const [mccList, merchantList, chainIdList] = await Promise.all([
        fetchMccList(page, perPage, search),
        fetchMerchantList(page, perPage, search),
        fetchChainIdList(page, perPage, search),
      ])
      return { mccList, merchantList, chainIdList }
    },
    onError: error => {
      console.error('Error fetching reference lists:', error.message)
    },
  })
}
